/**
 * Indicator script evaluation pieces shared by the main thread (util/chart createIndicatorData) and the
 * indicator Web Worker. Keep this module free of app/runtime imports (store, React, API clients): it is
 * loaded inside the worker. Type-only imports are fine.
 */
import type { ChartCandle } from '../components/chartV2/chart.typings'
import { decodeIndicatorOutputSource, resolveIndicatorInputValue, type IndicatorOutputSourceRef } from './indicators'

interface InputSeriesParams {
    title: string,
    defval: string,
    code?: string
}

export type ResolveIndicatorOutputSeries = (ref: IndicatorOutputSourceRef, candleCount: number) => number[] | undefined

/** Candle-derived series and the `input` API exposed to indicator scripts. */
export const buildIndicatorScriptInputs = (
    candles: ChartCandle[],
    inputValues: Record<string, string>,
    resolveIndicatorOutputSeries?: ResolveIndicatorOutputSeries
) => {
    const open: number[] = []
    const high: number[] = []
    const low: number[] = []
    const close: number[] = []
    const hl2: number[] = []
    const hlc3: number[] = []
    const ohlc4: number[] = []
    const hlcc4: number[] = []
    const tbt: number[] = []
    const volume: number[] = []
    const vwap: number[] = []
    const impliedVolatility: number[] = []
    const openInterest: number[] = []
    const callOpenInterest: number[] = []
    const putOpenInterest: number[] = []
    const timestamps: number[] = []
    const oiChange: number[] = []
    const deliveryPercentage: number[] = []
    let lastValidDeliveryPercentage = NaN
    let prevOi: number = 0
    candles.forEach(c => {
        open.push(c.open)
        high.push(c.high)
        low.push(c.low)
        close.push(c.close)
        hl2.push((c.high + c.low) / 2)
        hlc3.push((c.high + c.low + c.close) / 3)
        ohlc4.push((c.open+c.high+c.low+c.close)/4)
        hlcc4.push((c.high+c.low+c.close+c.close)/4)
        if (c.deliveryPercentage !== 0) {
            lastValidDeliveryPercentage = c.deliveryPercentage
        }
        deliveryPercentage.push(lastValidDeliveryPercentage)

        volume.push(c.volume)
        vwap.push(c.vwap)
        impliedVolatility.push(c.impliedVolatility)
        timestamps.push((c.time as number) * 1000)
        openInterest.push(c.openInterest)

        if (!isNaN(c.openInterest)) {
            if (prevOi) {
                oiChange.push(c.openInterest - prevOi)
            } else {
                oiChange.push(Number.NaN)
            }
            prevOi = c.openInterest
        } else {
            oiChange.push(0)
        }

        callOpenInterest.push(c.callOpenInterest)
        putOpenInterest.push(c.putOpenInterest)
    })

    let input = {
        int: (data: { title: string, defval: number, minval?: number, maxval?: number, code: string }) => {
            const value = resolveIndicatorInputValue(inputValues, data)
            return parseInt(value ?? '') || data.defval
        },
        string: (data: { title: string, defval: string, code?: string }) => {
            return resolveIndicatorInputValue(inputValues, data) ?? data.defval
        },
        float: (data: {
            title: string,
            defval: number,
            minval?: number,
            maxval?: number,
            code?: string
        }) => parseFloat(resolveIndicatorInputValue(inputValues, data) ?? '') || data.defval,
        bool: (data: { title: string, defval: boolean, code?: string }) => {
            const value = resolveIndicatorInputValue(inputValues, data)
            if (value === 'false') return false
            if (value === 'true') return true
            return value ? !!value : data.defval
        },
        source: (data: { title: string; defval: string; options: string[]; code?: string }) => {
            return resolveIndicatorInputValue(inputValues, data) ?? data.defval
        },
        series: (data: InputSeriesParams) => {
            const value = resolveIndicatorInputValue(inputValues, data) ?? data.defval
            const indicatorRef = decodeIndicatorOutputSource(value)
            if (indicatorRef) {
                const indicatorSeries = resolveIndicatorOutputSeries?.(indicatorRef, candles.length)
                if (indicatorSeries?.length) {
                    return indicatorSeries
                }
                throw new Error('Selected indicator source is unavailable')
            }
            switch (value) {
                case 'close':
                    return close
                case 'open':
                    return open
                case 'high':
                    return high
                case 'low':
                    return low
                case 'volume':
                    return volume
                case 'hl2':
                    return hl2
                case 'hlc3':
                    return hlc3
                case 'ohlc4':
                    return ohlc4
                case 'hlcc4':
                    return hlcc4
                case 'tbt':
                    return vwap
                default:
                    return close
            }
        }
    }

    return {
        input,
        open,
        high,
        low,
        close,
        volume,
        vwap,
        hl2,
        hlc3,
        ohlc4,
        hlcc4,
        tbt,
        impliedVolatility,
        openInterest,
        oiChange,
        callOpenInterest,
        putOpenInterest,
        timestamps,
        deliveryPercentage,
    }
}

/** Names (in order) of the variables an indicator script can reference. */
export const INDICATOR_SCRIPT_CONTEXT_KEYS = [
    'open', 'high', 'low', 'close', 'volume', 'vwap', 'impliedVolatility', 'openInterest',
    'oiChange', 'callOpenInterest', 'putOpenInterest', 'timestamps', 'input', 'ta', 'series', 'indicator',
    'plot', 'line', 'fill', 'draw', 'hline', 'deliveryPercentage', 'hlc3', 'hl2', 'ohlc4', 'hlcc4', 'calendarData', 'chartOffset',
] as const

type IndicatorScriptFn = (...args: unknown[]) => Promise<unknown>
const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor as new (...args: string[]) => IndicatorScriptFn
const compiledScripts = new Map<string, IndicatorScriptFn>()

/** Runs a script with the given context. Compiled functions are cached per script source (scripts run on every tick). */
export const runIndicatorScript = async (
    script: string,
    context: Record<(typeof INDICATOR_SCRIPT_CONTEXT_KEYS)[number], unknown>,
    thisArg?: unknown
): Promise<void> => {
    let fn = compiledScripts.get(script)
    if (!fn) {
        fn = new AsyncFunction(...INDICATOR_SCRIPT_CONTEXT_KEYS, script)
        compiledScripts.set(script, fn)
    }
    await fn.apply(thisArg, INDICATOR_SCRIPT_CONTEXT_KEYS.map((key) => context[key]))
}
